"use client";

import { Children, type Key, type ReactNode, type RefObject, useCallback, useEffect, useState } from "react";

import { type VariantProps, cva } from "class-variance-authority";

import { clsx } from "../../utils/clsx";
import { randomUUID } from "../../utils/uuid";
import { type DiffItem, computeDiff } from "./diff";
import { TransitionGroupItem } from "./transition-group-item";

const transitionGroupAnimationVariants = cva("motion-reduce:!animate-none", {
  variants: {
    animation: {
      custom: "",
      expand: "data-enter:animate-[expand-enter_0.2s] data-leave:animate-[expand-leave_0.2s]",
      expandHorizontal: "data-enter:animate-[expand-horizontal-enter_0.2s] data-leave:animate-[expand-horizontal-leave_0.2s]",
      expandFade: "data-enter:animate-[expand-fade-enter_0.3s] data-leave:animate-[expand-fade-leave_0.3s]",
      scale:
        "data-enter:animate-[expand-enter_0.2s] data-leave:animate-[expand-leave_0.2s] data-enter:[&>*]:animate-[scale-enter_0.2s] data-leave:[&>*]:animate-[scale-leave_0.2s]",
    },
  },
  defaultVariants: {
    animation: "expand",
  },
});

interface TransitionGroupProps extends VariantProps<typeof transitionGroupAnimationVariants> {
  children?: ReactNode;
  className?: string;
  duration?: number;
  wrapper?: (props: { children: ReactNode }) => ReactNode;
  containerRef?: RefObject<HTMLElement | null>;
  horizontalGap?: number;
  verticalGap?: number;
}

interface TransitionGroupEntry {
  id: string;
  key: Key;
  children?: ReactNode;
  stage: DiffItem["action"];
}

export const TransitionGroup = ({
  children,
  animation,
  duration,
  className,
  wrapper,
  containerRef,
  horizontalGap,
  verticalGap,
}: TransitionGroupProps) => {
  const [items, setItems] = useState<TransitionGroupEntry[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Initial items
  useEffect(() => {
    const mappedChildren =
      Children.map(children, (child, i) => ({
        id: randomUUID(),
        key: keyOfChild(child, i),
        children: child,
        stage: "keep",
      })) ?? [];
    setItems(mappedChildren.filter((child) => child.children) as TransitionGroupEntry[]);
  }, []);

  useEffect(() => {
    setItems((items) => mergeDiff(items, children, containerRef?.current));
  }, [children, containerRef]);

  const onAnimationEnd = useCallback((item: TransitionGroupEntry) => {
    if (item.stage === "add") {
      setItems((items) => items.map((i) => (i.id === item.id ? { ...i, stage: "keep" } : i)));
    } else if (item.stage === "delete") {
      setItems((items) => items.filter((i) => i.id !== item.id));
    }
  }, []);

  const transitionChildren = items.map((item) => (
    <TransitionGroupItem
      key={item.stage === "delete" ? `delete-${String(item.key)}` : `item-${String(item.key)}`}
      className={clsx(transitionGroupAnimationVariants({ animation }), className)}
      duration={duration}
      itemKey={item.key}
      stage={item.stage}
      onAnimationEnd={() => onAnimationEnd(item)}
      horizontalGap={horizontalGap}
      verticalGap={verticalGap}
    >
      {item.children}
    </TransitionGroupItem>
  ));

  if (!wrapper) {
    return transitionChildren;
  }

  return wrapper({
    children: transitionChildren,
  });
};

const mergeDiff = (previousItems: TransitionGroupEntry[], children: ReactNode, container?: HTMLElement | null): TransitionGroupEntry[] => {
  const previousChildrenMap = new Map(previousItems.map((item) => [item.key, item.children]));

  const nextChildrenEntries =
    Children.map(children, (child, i) => ({
      key: keyOfChild(child, i),
      child,
    })) ?? [];
  const nextChildrenMap = new Map(nextChildrenEntries.map((item) => [item.key, item.child]));

  const previousKeys = previousItems.map((item) => item.key) as Key[];
  const nextKeys = nextChildrenEntries.map((item) => item.key) as Key[];

  const diff = computeDiff(previousKeys, nextKeys);

  const output: TransitionGroupEntry[] = [];
  const alreadyDeletedKeys = new Set<Key>();

  for (const item of diff) {
    if (item.action === "delete" && container) {
      const element = container?.querySelector(`[data-transition-item-key="${CSS.escape(String(item.item))}"]`);

      if (!element) {
        continue;
      }
    }

    const previousItem = previousItems.find((i) => i.key === item.item);
    const previousStage = previousItem?.stage;

    if (item.action === "delete") {
      if (alreadyDeletedKeys.has(item.item)) {
        continue;
      }
      alreadyDeletedKeys.add(item.item);
    }

    output.push({
      id: previousItem && item.action !== "delete" ? previousItem?.id : randomUUID(),
      key: item.item,
      children: nextChildrenMap.get(item.item) ?? previousChildrenMap.get(item.item),
      stage: previousStage === "add" && item.action === "keep" ? "add" : item.action,
    });
  }

  return output.filter((item) => item?.children) as TransitionGroupEntry[];
};

const keyOfChild = (child: ReactNode, fallback: Key): Key => {
  if (child != null && typeof child === "object" && "key" in child) {
    const key = child.key;
    if (typeof key === "string" || typeof key === "number" || typeof key === "bigint") {
      return key;
    }
  }
  return fallback;
};
